import { YES_NO, applicantSection, feeInfo, otherAttachments, replySection } from "modules/ebiForms/common";
import { socialProxyAttachment, socialRepresentationSection } from "modules/ebiForms/definitions/sharedSocial";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * C5. Asystent rodziny — dwa tryby (wariant wniosku w BE):
 * - „Za życiem”: wniosek kobiety w ciąży lub rodziny o koordynację poradnictwa przez asystenta rodziny — art. 8 ust. 2–7
 *   ustawy z 4.11.2016 r. o wsparciu kobiet w ciąży i rodzin „Za życiem” (bez wywiadu; wniosek zawiera zgodę na
 *   przetwarzanie danych — art. 8 ust. 7). Układ i brzmienie jak wzór MOPS Szczecinek
 *   (https://samorzad.gov.pl/attachment/3b00b08e-d2f7-4efd-8bc7-09bc4f3484e1, pobrany 22.10.2026: dane osoby, „Wnioskuję
 *   o wsparcie asystenta rodziny, o którym mowa w art. 8 ust. 2–7…”, zaświadczenie z art. 4 ust. 3, pouczenie, zgoda).
 * - art. 11 ustawy z 9.06.2011 r. o wspieraniu rodziny i systemie pieczy zastępczej: nie ma formalnego wniosku rodziny —
 *   rodzina zgłasza potrzebę wsparcia i zgodę na współpracę, a przydział poprzedza wywiad i analiza pracownika socjalnego;
 *   zgłoszenie bez załączników (dokument „Pismo”).
 */
const ZA_ZYCIEM: EbiCondition = { field: "tryb", equals: "zaZyciem" };
const ART_11: EbiCondition = { field: "tryb", equals: "art11" };

const C5: IEbiFormDefinition = {
  code: "C5",
  version: 1,
  title: "Wniosek o przydzielenie asystenta rodziny",
  category: EEbiFormCategory.Social,
  office: EEbiFormOffice.MunicipalUnit,
  description: "Asystent rodziny dla kobiety w ciąży lub rodziny z dzieckiem z programu „Za życiem” albo zgłoszenie potrzeby wsparcia asystenta.",
  keywords: ["asystent rodziny", "za życiem", "wsparcie rodziny", "ciąża", "ops", "piecza zastępcza"],
  organ: "Kierownik ośrodka pomocy społecznej / dyrektor centrum usług społecznych",
  legalBasis:
    "art. 8 ust. 2–7 ustawy z dnia 4 listopada 2016 r. o wsparciu kobiet w ciąży i rodzin „Za życiem”; art. 11 ustawy z dnia 9 czerwca 2011 r. o wspieraniu rodziny i systemie pieczy zastępczej",
  request: "Wnoszę o objęcie mnie i mojej rodziny wsparciem asystenta rodziny w trybie wskazanym we wniosku.",
  feeLabel: "bez opłaty",
  fee: "Bez opłaty.",
  variantField: "tryb",
  variants: {
    zaZyciem: { title: "Wniosek o przydzielenie asystenta rodziny w trybie ustawy „Za życiem”", documentType: "Wniosek" },
    art11: { title: "Zgłoszenie potrzeby wsparcia asystenta rodziny", documentType: "Pismo" },
  },
  sections: [
    {
      code: "trybWsparcia",
      title: "Tryb wsparcia",
      fields: [
        {
          code: "tryb",
          type: "radio",
          label: "Składam",
          required: true,
          options: [
            {
              value: "zaZyciem",
              label:
                "wniosek o wsparcie asystenta rodziny, o którym mowa w art. 8 ust. 2–7 ustawy „Za życiem” — dla kobiety w ciąży albo rodziny z dzieckiem z zaświadczeniem lekarskim z art. 4 ust. 3 tej ustawy",
            },
            {
              value: "art11",
              label: "zgłoszenie potrzeby wsparcia asystenta rodziny — rodzina ma trudności w wypełnianiu funkcji opiekuńczo-wychowawczych",
            },
          ],
        },
      ],
    },
    applicantSection("Dane osoby ubiegającej się o wsparcie"),
    socialRepresentationSection(),
    {
      code: "zaZyciem",
      title: "Wsparcie w trybie ustawy „Za życiem”",
      visibleWhen: ZA_ZYCIEM,
      fields: [
        {
          code: "podstawa",
          type: "radio",
          label: "Wsparcie dotyczy",
          required: true,
          options: [
            { value: "ciaza", label: "kobiety w ciąży — posiadam dokument potwierdzający ciążę" },
            {
              value: "dziecko",
              label:
                "rodziny z dzieckiem, które ma zaświadczenie o ciężkim i nieodwracalnym upośledzeniu albo nieuleczalnej chorobie zagrażającej życiu, powstałych w prenatalnym okresie rozwoju dziecka lub w czasie porodu",
            },
          ],
        },
        {
          code: "upowaznienie",
          type: "radio",
          label: "Czy asystent rodziny ma występować w Twoim imieniu do podmiotów udzielających wsparcia (na podstawie pisemnego upoważnienia)?",
          required: true,
          options: YES_NO,
        },
        feeInfo(
          "infoPouczenie",
          "Rodzina jest uprawniona do poradnictwa w zakresie: przezwyciężania trudności w pielęgnacji i wychowaniu dziecka, wsparcia psychologicznego, pomocy prawnej (w szczególności praw rodzicielskich i uprawnień pracowniczych) oraz dostępu do rehabilitacji społecznej i zawodowej i świadczeń opieki zdrowotnej. Poradnictwo koordynuje asystent rodziny: opracowuje wspólnie z wnioskodawcą katalog możliwego wsparcia i — na żądanie wnioskodawcy, na podstawie pisemnego upoważnienia — występuje w jego imieniu do podmiotów leczniczych, samorządu i jednostek wspierania rodziny.",
        ),
        {
          code: "pouczenie",
          type: "declaration",
          label: "Oświadczam, że zapoznałam/zapoznałem się z powyższym pouczeniem.",
          required: true,
        },
        {
          code: "zgodaDane",
          type: "declaration",
          label:
            "Zgodnie z art. 8 ust. 7 ustawy „Za życiem” złożenie wniosku jest jednoznaczne z wyrażeniem zgody na przetwarzanie danych osobowych przez ośrodek pomocy społecznej (centrum usług społecznych), w tym asystenta rodziny, niezbędnych do wykonywania zadań wynikających z tej ustawy oraz na przekazanie asystentowi rodziny przez podmioty lecznicze, jednostki samorządu terytorialnego i jednostki organizacyjne wspierania rodziny informacji o udzielonym wsparciu.",
          required: true,
        },
        {
          code: "prawdziwosc",
          type: "declaration",
          label: "Oświadczam, że dane zawarte we wniosku są prawdziwe.",
          required: true,
        },
      ],
    },
    {
      code: "art11",
      title: "Zgłoszenie potrzeby wsparcia",
      visibleWhen: ART_11,
      fields: [
        feeInfo(
          "infoArt11",
          "Asystenta rodziny przydziela kierownik ośrodka po przeprowadzeniu przez pracownika socjalnego wywiadu środowiskowego i analizie sytuacji rodziny (art. 11 ustawy o wspieraniu rodziny i systemie pieczy zastępczej). Zgłoszenie nie wymaga załączników — pracownik socjalny skontaktuje się z rodziną.",
        ),
        {
          code: "opisSytuacji",
          type: "textarea",
          label: "W czym rodzina potrzebuje wsparcia",
          required: true,
          maxLength: 5000,
          hint: "Krótko opisz trudności, np. w opiece nad dziećmi, wychowaniu, prowadzeniu domu.",
        },
        {
          code: "zgodaWspolpraca",
          type: "declaration",
          label: "Wyrażam zgodę na współpracę z asystentem rodziny.",
          required: true,
        },
      ],
    },
    replySection(),
  ],
  attachments: [
    {
      code: "zaswiadczenieArt4",
      label:
        "Zaświadczenie lekarskie potwierdzające ciężkie i nieodwracalne upośledzenie albo nieuleczalną chorobę zagrażającą życiu, powstałe w prenatalnym okresie rozwoju dziecka lub w czasie porodu (art. 4 ust. 3 ustawy „Za życiem”)",
      hint: "Wystawione przez lekarza ubezpieczenia zdrowotnego ze specjalizacją II stopnia lub tytułem specjalisty w dziedzinie położnictwa i ginekologii, perinatologii, neonatologii, neurologii dziecięcej, kardiologii dziecięcej lub chirurgii dziecięcej.",
      visibleWhen: { all: [ZA_ZYCIEM, { field: "podstawa", equals: "dziecko" }] },
      required: true,
    },
    {
      code: "dokumentCiaza",
      label: "Dokument potwierdzający ciążę (zaświadczenie lekarza lub położnej)",
      visibleWhen: { all: [ZA_ZYCIEM, { field: "podstawa", equals: "ciaza" }] },
      required: true,
    },
    {
      code: "upowaznienieAsystenta",
      label: "Pisemne upoważnienie asystenta rodziny do występowania w imieniu wnioskodawcy (art. 8 ust. 3 pkt 2)",
      visibleWhen: { all: [ZA_ZYCIEM, { field: "upowaznienie", equals: "tak" }] },
      required: true,
    },
    socialProxyAttachment(),
    otherAttachments(),
  ],
};

export default C5;
